import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Check, Palette, Scissors, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSwatchFilter, SwatchFilterBar, LoadMore } from './SwatchFilter';
import { SectionShell, SectionHeader } from '../shared';
import { addSampleColor } from '../../../lib/sampleRequest';
import { resolveImageUrl } from '../../../lib/imageCache';
import { CONFIG_PRODUCTS, productsFor, configureUrl } from './fabricProducts';

const API = process.env.REACT_APP_BACKEND_URL;

const showCode = (c) => c.code && !c.name.startsWith(c.code);
// Display-only type labels; catalog values stay unchanged.
const TYPE_LABELS = { 'Black-Out': 'Blackout' };
const publicLabels = (c) => ({ ...c, fabric_type: TYPE_LABELS[c.fabric_type] || c.fabric_type, collection: TYPE_LABELS[c.collection] || c.collection });

const Swatch = ({ color, className = '' }) => (
  color.image_url
    ? <img src={resolveImageUrl(color.image_url, 512)} alt={`${color.name} fabric swatch`} loading="lazy" className={`w-full h-full object-cover ${className}`} />
    : <div className={`w-full h-full ${className}`} style={{ backgroundColor: color.hex_color || '#e5e5e5' }} />
);

const FabricTile = ({ color, selected, onSelect }) => (
  <button type="button" onClick={() => onSelect(color)} data-testid={`fabric-tile-${color.id}`}
    className={`group text-left rounded-xl overflow-hidden border bg-white transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md ${selected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-neutral-200'}`}>
    <div className="relative aspect-square bg-neutral-100 overflow-hidden">
      <Swatch color={color} className="transition-transform duration-500 group-hover:scale-105" />
      {selected && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></span>}
    </div>
    <div className="p-2.5">
      <p className="text-sm font-medium text-neutral-900 truncate">{color.name}</p>
      <p className="text-[11px] text-neutral-500 truncate">{showCode(color) ? `${color.code} · ` : ''}{color.collection}</p>
    </div>
  </button>
);

const FabricDetail = ({ color, onClose }) => {
  const { primary, others } = productsFor(color);
  return (
    <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 md:gap-10 bg-white rounded-2xl border border-neutral-200 p-4 md:p-6 mb-8 shadow-sm" data-testid="fabric-detail">
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-neutral-100" data-testid="fabric-closeup">
        {color.image_url
          ? <img src={resolveImageUrl(color.image_url, 1024)} alt={`${color.name} texture close-up`} className="absolute inset-0 w-full h-full object-cover scale-[1.6]" />
          : <Swatch color={color} />}
        <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-md text-[11px] font-medium text-neutral-700">Texture close-up</span>
      </div>
      <div className="flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">{color.collection}</p>
            <h3 className="text-2xl md:text-3xl text-neutral-900" data-testid="fabric-detail-name">{color.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-500" aria-label="Close fabric details" data-testid="fabric-detail-close"><X className="w-4 h-4" /></button>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {color.code && <div><dt className="text-neutral-500 text-xs">Code</dt><dd className="font-medium text-neutral-900" data-testid="fabric-detail-code">{color.code}</dd></div>}
          {color.fabric_type && <div><dt className="text-neutral-500 text-xs">Type</dt><dd className="font-medium text-neutral-900">{color.fabric_type}</dd></div>}
          {color.color_families?.length > 0 && <div><dt className="text-neutral-500 text-xs">Color {color.color_families.length > 1 ? 'families' : 'family'}</dt><dd className="font-medium text-neutral-900" data-testid="fabric-detail-family">{color.color_families.join(', ')}</dd></div>}
          {color.brand && <div><dt className="text-neutral-500 text-xs">Brand</dt><dd className="font-medium text-neutral-900">{color.brand}</dd></div>}
        </dl>
        <Link to={configureUrl(primary, color)} data-testid="configure-fabric-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors self-start">
          Configure This Fabric <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="mt-2 text-xs text-neutral-500" data-testid="configure-fabric-product">Opens {CONFIG_PRODUCTS[primary]} with this fabric selected.</p>
        {others.length > 0 && (
          <p className="mt-3 text-xs text-neutral-600" data-testid="configure-fabric-alternatives">
            Also available as{' '}
            {others.map((t, i) => (
              <span key={t}>{i > 0 && ', '}<Link to={configureUrl(t, color)} className="text-amber-700 hover:underline" data-testid={`configure-fabric-as-${t}`}>{CONFIG_PRODUCTS[t]}</Link></span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
};

const SamplesPrompt = ({ selected }) => {
  const navigate = useNavigate();
  const order = () => {
    if (selected) {
      addSampleColor({ id: selected.id, name: selected.name, code: selected.code, hex_color: selected.hex_color, image_url: selected.image_url, brand_name: selected.brand, collection_name: selected.collection, material_name: selected.material });
    }
    navigate('/request-samples');
    if (selected) setTimeout(() => toast.success(`${selected.name} added to your sample request`), 400);
  };
  return (
    <div className="mt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-neutral-50 border border-neutral-200 px-6 py-6" data-testid="samples-prompt">
      <div>
        <h3 className="text-xl text-neutral-900">Prefer to see it in person?</h3>
        <p className="text-sm text-neutral-600 mt-1">{selected ? `Order a sample of ${selected.name}, and add more on the next page.` : 'Order fabric samples and compare them in your own light.'}</p>
      </div>
      <button type="button" onClick={order} data-testid="order-samples-btn"
        className="inline-flex items-center justify-center gap-2 border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white px-6 py-3 rounded-full text-sm font-medium transition-colors shrink-0">
        <Scissors className="w-4 h-4" /> Order Samples
      </button>
    </div>
  );
};

export const FabricVisualizerSection = ({ section: sectionProp }) => {
  const section = { id: 'fabric-visualizer', type: 'fabric_visualizer', style: {}, visibility: {}, ...(sectionProp || {}) };
  const p = section.props || {};
  const [colors, setColors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const detailRef = useRef(null);
  const f = useSwatchFilter(colors);

  const location = useLocation();
  useEffect(() => {
    fetch(`${API}/api/fabric-collections/library`).then((r) => r.json()).then((list) => setColors(list.map(publicLabels))).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  // Site search links: ?fabric=<id> opens that fabric; ?fabric_q= / ?fabric_mat= (exact material) / ?fabric_coll= prefill the filters (also on same-page navigation)
  useEffect(() => {
    if (!colors.length) return;
    const params = new URLSearchParams(location.search);
    const want = params.get('fabric');
    const hit = want && colors.find((c) => c.id === want);
    if (hit) { setSelected(hit); if (!params.get('fabric_q')) f.clearAll(); setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }
    const fm = params.get('fabric_mat');
    const fq = params.get('fabric_q') ?? fm;
    if (fq !== null || params.get('fabric_coll') !== null) {
      f.clearAll();
      if (fq) f.setQuery(fq);
      if (fm) f.setMaterial(fm);
      if (params.get('fabric_coll')) f.setCollection(TYPE_LABELS[params.get('fabric_coll')] || params.get('fabric_coll'));
      setTimeout(() => document.getElementById('fabric-finder')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.search, colors.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (c) => {
    setSelected(c);
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  };

  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={section.style || {}} />
      <div id="fabric-finder" className="mt-8 md:mt-12 max-w-6xl mx-auto scroll-mt-24" data-testid="fabric-finder">
        <div ref={detailRef} className="scroll-mt-24">
          {selected && <FabricDetail color={selected} onClose={() => setSelected(null)} />}
        </div>
        {loaded && !colors.length ? (
          <div className="text-center py-16 text-neutral-400"><Palette className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>Fabrics are loading soon.</p></div>
        ) : (
          <>
            <SwatchFilterBar f={f} total={colors.length} />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="fabric-grid">
              {f.visible.map((c) => <FabricTile key={c.id} color={c} selected={selected?.id === c.id} onSelect={select} />)}
            </div>
            {loaded && !f.visible.length && <p className="text-sm text-neutral-500 py-6 text-center" data-testid="swatch-empty">No fabrics match your search.</p>}
            <LoadMore f={f} />
          </>
        )}
        <SamplesPrompt selected={selected} />
      </div>
    </SectionShell>
  );
};
